export type ProductCogsInterval = {
    unitCogs: number;
    startDate: string | Date;
    endDate?: string | Date | null;
};

export type ProductDateBounds = {
    startDate?: string | Date | null;
    endDate?: string | Date | null;
};

export type DateParts = {
    year: number;
    month: number;
    day: number;
};

export type FiscalMonthColumn = {
    year: number;
    month: number;
    field: string;
    label: string;
};

export type MonthlyCogsCell = {
    value: number | null;
    hasConflict: boolean;
    entries: ProductCogsInterval[];
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function toDateParts(value: string | Date | null | undefined): DateParts | null {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return {
                year: Number(match[1]),
                month: Number(match[2]),
                day: Number(match[3]),
            };
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
    };
}

export function compareDateParts(left: DateParts, right: DateParts): number {
    if (left.year !== right.year) {
        return left.year - right.year;
    }
    if (left.month !== right.month) {
        return left.month - right.month;
    }
    return left.day - right.day;
}

export function lastDayOfMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

export function fiscalYearForDate(parts: DateParts, startMonth: number): number {
    return parts.month >= startMonth ? parts.year : parts.year - 1;
}

export function getCurrentFiscalYear(startMonth: number, today: Date = new Date()): number {
    const resolvedStartMonth = startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
    return fiscalYearForDate(
        { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() },
        resolvedStartMonth
    );
}

export function getFiscalMonthColumns(startMonth: number, year: number): FiscalMonthColumn[] {
    const resolvedStartMonth = startMonth >= 1 && startMonth <= 12 ? startMonth : 1;

    return Array.from({ length: 12 }, (_, index) => {
        const monthOffset = resolvedStartMonth - 1 + index;
        const month = (monthOffset % 12) + 1;
        const columnYear = year + Math.floor(monthOffset / 12);
        const paddedMonth = String(month).padStart(2, '0');

        return {
            year: columnYear,
            month,
            field: `cogs_${columnYear}_${paddedMonth}`,
            label: `${MONTH_LABELS[month - 1]}-${String(columnYear).slice(-2)}`,
        };
    });
}

export function formatFiscalYearLabel(startMonth: number, year: number): string {
    const resolvedStartMonth = startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
    if (resolvedStartMonth === 1) {
        return String(year);
    }

    return `${year}-${year + 1}`;
}

export function getAvailableFiscalYears(
    products: Array<{ cogs?: ProductCogsInterval[] }>,
    startMonth: number,
    currentFiscalYear: number
): number[] {
    const years = new Set<number>([currentFiscalYear]);

    for (const product of products) {
        for (const cogs of product.cogs ?? []) {
            const parts = toDateParts(cogs.startDate);
            if (parts) {
                years.add(fiscalYearForDate(parts, startMonth));
            }
        }
    }

    const sorted = Array.from(years).sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const result: number[] = [];
    for (let fiscalYear = min; fiscalYear <= max; fiscalYear += 1) {
        result.push(fiscalYear);
    }
    return result;
}

export function resolveCogsForMonth(
    cogs: ProductCogsInterval[] | null | undefined,
    year: number,
    month: number,
    product?: ProductDateBounds
): MonthlyCogsCell {
    const emptyCell: MonthlyCogsCell = { value: null, hasConflict: false, entries: [] };
    const monthStart: DateParts = { year, month, day: 1 };
    const monthEnd: DateParts = { year, month, day: lastDayOfMonth(year, month) };

    if (product) {
        const productStart = toDateParts(product.startDate);
        const productEnd = toDateParts(product.endDate);

        if (productStart && compareDateParts(productStart, monthEnd) > 0) {
            return emptyCell;
        }
        if (productEnd && compareDateParts(productEnd, monthStart) < 0) {
            return emptyCell;
        }
    }

    const intervals = (cogs ?? []).filter((interval) => toDateParts(interval.startDate));

    const startedInMonth = intervals
        .filter((interval) => {
            const start = toDateParts(interval.startDate);
            return start !== null && start.year === year && start.month === month;
        })
        .sort((left, right) =>
            compareDateParts(toDateParts(left.startDate)!, toDateParts(right.startDate)!)
        );

    const startedByMonthEnd = intervals
        .filter((interval) => {
            const start = toDateParts(interval.startDate);
            return start !== null && compareDateParts(start, monthEnd) <= 0;
        })
        .sort((left, right) =>
            compareDateParts(toDateParts(right.startDate)!, toDateParts(left.startDate)!)
        );

    const displayed = startedByMonthEnd[0];

    return {
        value: displayed == null ? null : Number(displayed.unitCogs),
        hasConflict: startedInMonth.length > 1,
        entries: startedInMonth,
    };
}
